// src/components/IntensiveTeachingPresentation.js
// Teaching slideshow modal for Pre-Marriage Intensive modules
// Visual Theme: Emerald/Teal with Gold accents

import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import { INTENSIVE_TEACHING } from '../data/intensiveTeaching';

const IntensiveTeachingPresentation = ({ moduleNumber, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const teachingData = INTENSIVE_TEACHING[moduleNumber];

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowRight' || e.key === ' ') {
      e.preventDefault();
      nextSlide();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prevSlide();
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [currentSlide]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!teachingData || !teachingData.slides) {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 text-center max-w-md">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Teaching Not Available</h2>
          <p className="text-gray-600 mb-6">Teaching content for this module is not available yet.</p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const slides = teachingData.slides;
  const slide = slides[currentSlide];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const renderSlide = () => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 sm:px-8">
            <div className="text-4xl sm:text-5xl md:text-6xl mb-6">📖</div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              {slide.title}
            </h1>
            {slide.subtitle && (
              <h2 className="text-lg sm:text-xl md:text-2xl text-emerald-200 mb-6">
                {slide.subtitle}
              </h2>
            )}
            {slide.scripture && (
              <div className="max-w-2xl mx-auto p-4 sm:p-6 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                <p className="text-sm sm:text-base md:text-lg text-amber-200 italic leading-relaxed">
                  {slide.scripture}
                </p>
              </div>
            )}
            {slide.highlight && (
              <div className="mt-6 max-w-xl mx-auto">
                <p className="text-base sm:text-lg text-emerald-100 font-medium">
                  💡 {slide.highlight}
                </p>
              </div>
            )}
          </div>
        );

      case 'content':
        return (
          <div className="flex flex-col h-full px-4 sm:px-6 md:px-8 py-4 overflow-y-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              {slide.title}
            </h2>
            
            {slide.points && (
              <div className="flex-1 space-y-3 sm:space-y-4 max-w-3xl mx-auto w-full">
                {slide.points.map((point, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-white/20 hover:bg-white/15 transition"
                  >
                    <p className="text-sm sm:text-base md:text-lg text-white leading-relaxed">
                      {point}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {slide.scripture && (
              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-amber-900/30 rounded-lg border border-amber-500/30 max-w-3xl mx-auto w-full">
                <p className="text-sm sm:text-base text-amber-200 italic text-center">
                  {slide.scripture}
                </p>
              </div>
            )}
          </div>
        );

      case 'reflection':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 sm:px-8">
            <div className="text-4xl sm:text-5xl mb-6">🪞</div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-6">
              {slide.title || 'Reflection'}
            </h2>
            
            {slide.questions && (
              <div className="space-y-4 max-w-2xl">
                {slide.questions.map((question, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20"
                  >
                    <p className="text-base sm:text-lg md:text-xl text-emerald-100 leading-relaxed">
                      {question}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {slide.action && (
              <div className="mt-6 p-4 bg-amber-900/30 rounded-lg border border-amber-500/30 max-w-xl">
                <p className="text-sm sm:text-base text-amber-200">
                  <strong>Action Step:</strong> {slide.action}
                </p>
              </div>
            )}
          </div>
        );

      case 'action':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 sm:px-8">
            <div className="text-4xl sm:text-5xl mb-6">🎯</div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-6">
              {slide.title || 'Action Steps'}
            </h2>
            
            {slide.steps && (
              <div className="space-y-3 max-w-2xl w-full">
                {slide.steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 text-left"
                  >
                    <span className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </span>
                    <p className="text-sm sm:text-base md:text-lg text-white leading-relaxed">
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="flex flex-col h-full px-4 sm:px-6 md:px-8 py-4 overflow-y-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              {slide.title}
            </h2>
            
            {slide.content && (
              <div className="flex-1 max-w-3xl mx-auto w-full">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 sm:p-6 border border-white/20">
                  <p className="text-sm sm:text-base md:text-lg text-white leading-relaxed whitespace-pre-line">
                    {slide.content}
                  </p>
                </div>
              </div>
            )}

            {slide.points && (
              <div className="flex-1 space-y-3 sm:space-y-4 max-w-3xl mx-auto w-full">
                {slide.points.map((point, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-white/20"
                  >
                    <p className="text-sm sm:text-base md:text-lg text-white leading-relaxed">
                      {point}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {slide.scripture && (
              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-amber-900/30 rounded-lg border border-amber-500/30 max-w-3xl mx-auto w-full">
                <p className="text-sm sm:text-base text-amber-200 italic text-center">
                  {slide.scripture}
                </p>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gradient-to-br from-emerald-800 via-emerald-900 to-teal-900">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 bg-black/20">
        <div className="flex items-center gap-2 sm:gap-3">
          <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-300" />
          <div>
            <h3 className="text-sm sm:text-base font-semibold text-white">
              Module {moduleNumber}: {teachingData.moduleName}
            </h3>
            <p className="text-xs text-emerald-300 hidden sm:block">
              {teachingData.factorName}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="text-xs sm:text-sm text-emerald-200">
            {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Slide Progress Bar */}
      <div className="h-1 bg-white/20">
        <div
          className="h-full bg-emerald-400 transition-all duration-300"
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>

      {/* Slide Content */}
      <div className="flex-1 overflow-hidden">
        {renderSlide()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between p-3 sm:p-4 bg-black/20">
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition ${
            currentSlide === 0
              ? 'bg-white/10 text-white/50 cursor-not-allowed'
              : 'bg-white/20 text-white hover:bg-white/30'
          }`}
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Previous</span>
        </button>

        {/* Slide Dots */}
        <div className="flex gap-1 sm:gap-1.5 overflow-x-auto max-w-[50%] justify-center">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition flex-shrink-0 ${
                index === currentSlide
                  ? 'bg-emerald-400'
                  : index < currentSlide
                    ? 'bg-emerald-600'
                    : 'bg-white/30'
              }`}
            />
          ))}
        </div>

        <button
          onClick={currentSlide === slides.length - 1 ? onClose : nextSlide}
          className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition"
        >
          <span className="hidden sm:inline">
            {currentSlide === slides.length - 1 ? 'Finish' : 'Next'}
          </span>
          <span className="sm:hidden">
            {currentSlide === slides.length - 1 ? '✓' : '→'}
          </span>
          {currentSlide < slides.length - 1 && (
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          )}
        </button>
      </div>

      {/* Keyboard hints */}
      <div className="hidden md:flex justify-center py-2 bg-black/10 text-xs text-emerald-300/60">
        <span>Use ← → arrow keys or spacebar to navigate • Press ESC to close</span>
      </div>
    </div>
  );
};

export default IntensiveTeachingPresentation;
